import React from 'react';
import {
  TabList,
  Tab,
  SelectTabEvent,
  SelectTabData,
  makeStyles,
  tokens,
  Button,
  Tooltip,
  Dialog,
  DialogSurface,
  DialogBody,
} from '@fluentui/react-components';
import {
  Navigation20Regular,
  ChevronLeft20Regular,
  ChevronRight20Regular,
  Person20Regular,
  Apps20Regular,
  Calendar20Regular,
  Book20Regular,
  PeopleTeam20Regular,
  Settings20Regular,
  AppGeneric20Regular,
  Briefcase20Regular,
  Dismiss24Regular,
} from '@fluentui/react-icons';
import { useLocation } from 'react-router-dom';
import { useIsSignedIn } from '../../Context/Auth/Context';
import { usePermissions } from '../../Context/Permissions/Context';
import {
  hasBlueprintManagementPermission,
  hasGuideManagementPermission,
  hasLeadManagementPermission,
  hasRoleManagemetPermission,
  hasWebflowManagementPermission,
  hasDeveloperPermission,
  hasCalendarManagementPermission,
  hasCalendarManagementAdminPermission,
  hasBlueprintBuilderPermission,
  hasPromptManagementPermission,
} from '../../Context/Permissions/utils';
import { PrivacyPolicyContent } from '../Legal/PrivacyPolicyContent';
import { TermsOfServiceContent } from '../Legal/TermsOfServiceContent';
import { User } from './User';

const useStyles = makeStyles({
  dashboardContainer: {
    display: 'flex',
    flexDirection: 'row',
    height: '100%',
    padding: `0 ${tokens.spacingHorizontalM}`,
  },
  navigationWrapper: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    transitionProperty: 'width, min-width',
    transitionDuration: '300ms',
    transitionTimingFunction: 'ease-in-out',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  navigationWrapperExpanded: {
    minWidth: '250px',
    width: '250px',
  },
  navigationWrapperCollapsed: {
    minWidth: '60px',
    width: '60px',
  },
  toggleButton: {
    margin: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalS}`,
    alignSelf: 'stretch',
  },
  tabList: {
    flex: 1,
    marginRight: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalM,
    overflow: 'hidden',
  },
  tabListCollapsed: {
    paddingRight: 0,
    marginRight: 0,
    '& button': {
      justifyContent: 'center',
      paddingLeft: '0',
      paddingRight: '0',
    },
  },
  tabContent: {
    flex: 1,
    overflow: 'auto',
    position: 'relative',
  },
  tabContentFullPage: {
    flex: 1,
    overflow: 'hidden',
    position: 'relative',
  },
  openPortalButton: {
    padding: tokens.spacingVerticalXXL,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalL,
    height: '100%',
  },
  dialogSurface: {
    maxWidth: '100vw',
    maxHeight: '100vh',
    width: '100vw',
    height: '100vh',
    padding: 0,
    borderRadius: 0,
  },
  dialogBody: {
    padding: 0,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: tokens.spacingVerticalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  modalTitle: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
  },
  modalContent: {
    flex: 1,
    overflow: 'hidden',
  },
});

// Lazy-loaded components
const LazyRecommendedProducts = React.lazy(() =>
  import('./RecommendedProducts').then((module) => ({
    default: module.RecommendedProducts,
  }))
);

const LazyBlueprintFlowTracker = React.lazy(() =>
  import('../BlueprintFlowTracker/BlueprintFlowTracker').then((module) => ({
    default: module.BlueprintFlowTracker,
  }))
);

const LazyMailTemplateGenerator = React.lazy(() =>
  import('../MailTemplateGenerator/MailTemplateGenerator').then((module) => ({
    default: module.MailTemplateGenerator,
  }))
);

const LazySpecialists = React.lazy(() =>
  import('../Specialists/Specialists').then((module) => ({
    default: module.Specialists,
  }))
);

const LazyExperiences = React.lazy(() =>
  import('../Experiences/Experiences').then((module) => ({
    default: module.Experiences,
  }))
);

const LazyLeads = React.lazy(() =>
  import('../Leads/Leads').then((module) => ({
    default: module.Leads,
  }))
);

const LazyPathToHolisticWellbeing = React.lazy(() =>
  import('../PathToHolisticWellbeing/PathToHolisticWellbeing').then((module) => ({
    default: module.PathToHolisticWellbeing,
  }))
);

const LazyBlueprintLite = React.lazy(() =>
  import('../BlueprintLite/BlueprintLite').then((module) => ({
    default: module.BlueprintLite,
  }))
);

const LazyGuides = React.lazy(() =>
  import('../Guides/Guides').then((module) => ({
    default: module.Guides,
  }))
);

const LazyGuidesV2 = React.lazy(() =>
  import('../GuidesV2/GuidesV2').then((module) => ({
    default: module.GuidesV2,
  }))
);

const LazyRecommendationGenerator = React.lazy(() =>
  import('../RecommendationGenerator/RecommendationGenerator').then((module) => ({
    default: module.RecommendationGenerator,
  }))
);

const LazyEnvironmentDebugView = React.lazy(() =>
  import('../EnvironmentDebugView/EnvironmentDebugView').then((module) => ({
    default: module.EnvironmentDebugView,
  }))
);

const LazyRoleManager = React.lazy(() =>
  import('../RoleManager/RoleManager').then((module) => ({
    default: module.RoleManager,
  }))
);

const LazyConfigManagement = React.lazy(() =>
  import('../ConfigManagement/ConfigManagement').then((module) => ({
    default: module.ConfigManagement,
  }))
);

const LazyCalendar = React.lazy(() =>
  import('../InternalPortal/Calendar').then((module) => ({
    default: module.Calendar,
  }))
);

const LazyCalendarAdmin = React.lazy(() =>
  import('../InternalPortal/Calendar/Admin/CalendarAdmin').then((module) => ({
    default: module.CalendarAdmin,
  }))
);

const LazyImpersonation = React.lazy(() =>
  import('../Impersonation/Impersonation').then((module) => ({
    default: module.Impersonation,
  }))
);

const LazyBackfillAssessment = React.lazy(() =>
  import('../BackfillAssessment/BackfillAssessment').then((module) => ({
    default: module.BackfillAssessment,
  }))
);

const LazyBlueprintBuilder = React.lazy(() =>
  import('../BlueprintBuilder/BlueprintBuilder').then((module) => ({
    default: module.BlueprintBuilder,
  }))
);

const LazyPromptManagement = React.lazy(() =>
  import('../PromptManagement/PromptManagement').then((module) => ({
    default: module.PromptManagement,
  }))
);

const LazyPathAndCircleEmbed = React.lazy(() =>
  import('../PathAndCircleEmbed').then((module) => ({
    default: module.PathAndCircleEmbed,
  }))
);

type TabKey =
  | 'User'
  | 'PrivacyPolicy'
  | 'TermsOfService'
  | 'RecommendedProducts'
  | 'Experiences'
  | 'Products'
  | 'Secrets'
  | 'Specialists'
  | 'BlueprintFlowController'
  | 'BlueprintBuilder'
  | 'BackfillAssessment'
  | 'PromptManagement'
  | 'MailTemplateGenerator'
  | 'Leads'
  | 'PathToHolisticWellbeing'
  | 'BlueprintLite'
  | 'Guides'
  | 'GuidesV2'
  | 'RecommendationGenerator'
  | 'RoleManager'
  | 'EnvironmentDebug'
  | 'ConfigManagement'
  | 'Calendar'
  | 'CalendarAdmin'
  | 'PathAndCircleAdmin'
  | 'Impersonation';

const getTabFromPathname = (pathname: string): TabKey => {
  if (pathname === '/privacy' || pathname === '/privacy-policy') {
    return 'PrivacyPolicy';
  }

  if (pathname === '/terms' || pathname === '/terms-of-service') {
    return 'TermsOfService';
  }

  return 'User';
};

export const Dashboard: React.FC = () => {
  const styles = useStyles();
  const location = useLocation();
  const permissions = usePermissions();
  const isSignedIn = useIsSignedIn();
  const [selectedTab, setSelectedTab] = React.useState<TabKey>(() =>
    getTabFromPathname(location.pathname)
  );
  const [impersonationUnlocked, setImpersonationUnlocked] = React.useState(false);
  const [blueprintBuilderEmail, setBlueprintBuilderEmail] = React.useState<string | undefined>(
    undefined
  );
  const [isNavCollapsed, setIsNavCollapsed] = React.useState<boolean>(() => {
    const stored = localStorage.getItem('navigationCollapsed');
    return stored === 'true';
  });
  const [isPathAndCircleModalOpen, setIsPathAndCircleModalOpen] = React.useState(false);

  // Persist navigation state
  const toggleNavigation = () => {
    setIsNavCollapsed((prev) => {
      const newValue = !prev;
      localStorage.setItem('navigationCollapsed', String(newValue));
      return newValue;
    });
  };

  // Get icon for tab
  const getTabIcon = (tabKey: TabKey) => {
    switch (tabKey) {
      case 'User':
        return <Person20Regular />;
      case 'PrivacyPolicy':
      case 'TermsOfService':
        return <Book20Regular />;
      case 'Experiences':
        return <Apps20Regular />;
      case 'Specialists':
        return <PeopleTeam20Regular />;
      case 'RecommendationGenerator':
        return <AppGeneric20Regular />;
      case 'Guides':
      case 'GuidesV2':
        return <Book20Regular />;
      case 'Calendar':
      case 'CalendarAdmin':
        return <Calendar20Regular />;
      case 'PathAndCircleAdmin':
        return <AppGeneric20Regular />;
      case 'BlueprintFlowController':
      case 'BlueprintBuilder':
      case 'BackfillAssessment':
      case 'PromptManagement':
        return <AppGeneric20Regular />;
      case 'Leads':
        return <Briefcase20Regular />;
      case 'RoleManager':
      case 'ConfigManagement':
      case 'EnvironmentDebug':
        return <Settings20Regular />;
      default:
        return <Navigation20Regular />;
    }
  };

  // Listen for impersonation unlock event from easter egg
  React.useEffect(() => {
    const handleImpersonationUnlock = () => {
      console.log('[Dashboard] Impersonation unlocked via easter egg!');
      setImpersonationUnlocked(true);
    };

    window.addEventListener('impersonation-unlocked', handleImpersonationUnlock as EventListener);

    return () => {
      window.removeEventListener(
        'impersonation-unlocked',
        handleImpersonationUnlock as EventListener
      );
    };
  }, []);

  // Listen for navigate-to-calendar event from OAuth callback
  React.useEffect(() => {
    const handleNavigateToCalendar = () => {
      console.log('[Dashboard] Received navigate-to-calendar event, switching to Calendar tab');
      setSelectedTab('Calendar');
    };

    window.addEventListener('navigate-to-calendar', handleNavigateToCalendar as EventListener);

    return () => {
      window.removeEventListener('navigate-to-calendar', handleNavigateToCalendar as EventListener);
    };
  }, []);

  // Listen for open-blueprint-builder event from AllBlueprintFlowDetails
  React.useEffect(() => {
    const handleOpenBlueprintBuilder = (event: Event) => {
      const customEvent = event as CustomEvent<{ email: string }>;
      console.log('[Dashboard] Opening Blueprint Builder for:', customEvent.detail.email);
      setBlueprintBuilderEmail(customEvent.detail.email);
      setSelectedTab('BlueprintBuilder');
    };

    window.addEventListener('open-blueprint-builder', handleOpenBlueprintBuilder as EventListener);

    return () => {
      window.removeEventListener(
        'open-blueprint-builder',
        handleOpenBlueprintBuilder as EventListener
      );
    };
  }, []);

  React.useEffect(() => {
    setSelectedTab(getTabFromPathname(location.pathname));
  }, [location.pathname]);

  const handleTabSelect = (event: SelectTabEvent, data: SelectTabData) => {
    setSelectedTab(data.value as TabKey);
  };

  const renderTabContent = () => {
    switch (selectedTab) {
      case 'User':
        return <User />;
      case 'PrivacyPolicy':
        return <PrivacyPolicyContent />;
      case 'TermsOfService':
        return <TermsOfServiceContent />;
      case 'RecommendedProducts':
        return (
          <React.Suspense fallback={<div>Loading...</div>}>
            <LazyRecommendedProducts />
          </React.Suspense>
        );
      case 'Experiences':
        return (
          <React.Suspense fallback={<div>Loading...</div>}>
            <LazyExperiences />
          </React.Suspense>
        );
      case 'Products':
        return <div>Products</div>;
      case 'Secrets':
        return <div>Secrets</div>;
      case 'Specialists':
        return (
          <React.Suspense fallback={<div>Loading...</div>}>
            <LazySpecialists />
          </React.Suspense>
        );
      case 'BlueprintFlowController':
        return (
          <React.Suspense fallback={<div>Loading...</div>}>
            <LazyBlueprintFlowTracker />
          </React.Suspense>
        );
      case 'MailTemplateGenerator':
        return (
          <React.Suspense fallback={<div>Loading...</div>}>
            <LazyMailTemplateGenerator />
          </React.Suspense>
        );
      case 'Leads':
        return (
          <React.Suspense fallback={<div>Loading...</div>}>
            <LazyLeads />
          </React.Suspense>
        );
      case 'PathToHolisticWellbeing':
        return (
          <React.Suspense fallback={<div>Loading...</div>}>
            <LazyPathToHolisticWellbeing />
          </React.Suspense>
        );
      case 'BlueprintLite':
        return (
          <React.Suspense fallback={<div>Loading...</div>}>
            <LazyBlueprintLite />
          </React.Suspense>
        );
      case 'Guides':
        return (
          <React.Suspense fallback={<div>Loading...</div>}>
            <LazyGuides />
          </React.Suspense>
        );
      case 'GuidesV2':
        return (
          <React.Suspense fallback={<div>Loading...</div>}>
            <LazyGuidesV2 />
          </React.Suspense>
        );
      case 'RecommendationGenerator':
        return (
          <React.Suspense fallback={<div>Loading...</div>}>
            <LazyRecommendationGenerator />
          </React.Suspense>
        );
      case 'RoleManager':
        return (
          <React.Suspense fallback={<div>Loading...</div>}>
            <LazyRoleManager />
          </React.Suspense>
        );
      case 'EnvironmentDebug':
        return (
          <React.Suspense fallback={<div>Loading...</div>}>
            <LazyEnvironmentDebugView />
          </React.Suspense>
        );
      case 'ConfigManagement':
        return (
          <React.Suspense fallback={<div>Loading...</div>}>
            <LazyConfigManagement />
          </React.Suspense>
        );
      case 'Calendar':
        return (
          <React.Suspense fallback={<div>Loading...</div>}>
            <LazyCalendar />
          </React.Suspense>
        );
      case 'CalendarAdmin':
        return (
          <React.Suspense fallback={<div>Loading...</div>}>
            <LazyCalendarAdmin />
          </React.Suspense>
        );
      case 'Impersonation':
        return (
          <React.Suspense fallback={<div>Loading...</div>}>
            <LazyImpersonation />
          </React.Suspense>
        );
      case 'PathAndCircleAdmin':
        return (
          <div className={styles.openPortalButton}>
            <Briefcase20Regular style={{ fontSize: '48px' }} />
            <Button
              appearance="primary"
              size="large"
              onClick={() => setIsPathAndCircleModalOpen(true)}
            >
              Open Path & Circle Admin Portal
            </Button>
            <div style={{ fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground2 }}>
              Launch the full-screen admin portal for Path & Circle
            </div>
          </div>
        );
      case 'BlueprintBuilder':
        return (
          <React.Suspense fallback={<div>Loading...</div>}>
            <LazyBlueprintBuilder initialEmail={blueprintBuilderEmail} />
          </React.Suspense>
        );
      case 'BackfillAssessment':
        return (
          <React.Suspense fallback={<div>Loading...</div>}>
            <LazyBackfillAssessment />
          </React.Suspense>
        );
      case 'PromptManagement':
        return (
          <React.Suspense fallback={<div>Loading...</div>}>
            <LazyPromptManagement />
          </React.Suspense>
        );
      default:
        return <User />;
    }
  };

  return (
    <div className={styles.dashboardContainer}>
      <div
        className={`${styles.navigationWrapper} ${
          isNavCollapsed ? styles.navigationWrapperCollapsed : styles.navigationWrapperExpanded
        }`}
      >
        <Tooltip
          content={isNavCollapsed ? 'Expand Navigation' : 'Collapse Navigation'}
          relationship="label"
        >
          <Button
            appearance="subtle"
            icon={isNavCollapsed ? <ChevronRight20Regular /> : <ChevronLeft20Regular />}
            onClick={toggleNavigation}
            className={styles.toggleButton}
            aria-label={isNavCollapsed ? 'Expand navigation' : 'Collapse navigation'}
          />
        </Tooltip>
        <TabList
          selectedValue={selectedTab}
          onTabSelect={handleTabSelect}
          className={`${styles.tabList} ${isNavCollapsed ? styles.tabListCollapsed : ''}`}
          vertical
          size="large"
        >
          {isNavCollapsed ? (
            <Tooltip content="User" relationship="label">
              <Tab value="User" icon={getTabIcon('User')}>
                {!isNavCollapsed && 'User'}
              </Tab>
            </Tooltip>
          ) : (
            <Tab value="User" icon={getTabIcon('User')}>
              User
            </Tab>
          )}
          {isNavCollapsed ? (
            <Tooltip content="Privacy Policy" relationship="label">
              <Tab value="PrivacyPolicy" icon={getTabIcon('PrivacyPolicy')}>
                {!isNavCollapsed && 'Privacy Policy'}
              </Tab>
            </Tooltip>
          ) : (
            <Tab value="PrivacyPolicy" icon={getTabIcon('PrivacyPolicy')}>
              Privacy Policy
            </Tab>
          )}
          {isNavCollapsed ? (
            <Tooltip content="Terms of Service" relationship="label">
              <Tab value="TermsOfService" icon={getTabIcon('TermsOfService')}>
                {!isNavCollapsed && 'Terms of Service'}
              </Tab>
            </Tooltip>
          ) : (
            <Tab value="TermsOfService" icon={getTabIcon('TermsOfService')}>
              Terms of Service
            </Tab>
          )}
          {isSignedIn && (
            <>
              {hasWebflowManagementPermission(permissions) && (
                <>
                  {isNavCollapsed ? (
                    <Tooltip content="Experiences" relationship="label">
                      <Tab value="Experiences" icon={getTabIcon('Experiences')}>
                        {!isNavCollapsed && 'Experiences'}
                      </Tab>
                    </Tooltip>
                  ) : (
                    <Tab value="Experiences" icon={getTabIcon('Experiences')}>
                      Experiences
                    </Tab>
                  )}
                  {isNavCollapsed ? (
                    <Tooltip content="Specialists/Insights" relationship="label">
                      <Tab value="Specialists" icon={getTabIcon('Specialists')}>
                        {!isNavCollapsed && 'Specialists/Insights'}
                      </Tab>
                    </Tooltip>
                  ) : (
                    <Tab value="Specialists" icon={getTabIcon('Specialists')}>
                      Specialists/Insights
                    </Tab>
                  )}
                  {isNavCollapsed ? (
                    <Tooltip content="Recommendation Generator" relationship="label">
                      <Tab
                        value="RecommendationGenerator"
                        icon={getTabIcon('RecommendationGenerator')}
                      >
                        {!isNavCollapsed && 'Recomm. Gen'}
                      </Tab>
                    </Tooltip>
                  ) : (
                    <Tab
                      value="RecommendationGenerator"
                      icon={getTabIcon('RecommendationGenerator')}
                    >
                      Recomm. Gen
                    </Tab>
                  )}
                </>
              )}
              {hasGuideManagementPermission(permissions) && (
                <>
                  {isNavCollapsed ? (
                    <Tooltip content="Guides" relationship="label">
                      <Tab value="Guides" icon={getTabIcon('Guides')}>
                        {!isNavCollapsed && 'Guides'}
                      </Tab>
                    </Tooltip>
                  ) : (
                    <Tab value="Guides" icon={getTabIcon('Guides')}>
                      Guides
                    </Tab>
                  )}
                  {isNavCollapsed ? (
                    <Tooltip content="Guides V2" relationship="label">
                      <Tab value="GuidesV2" icon={getTabIcon('GuidesV2')}>
                        {!isNavCollapsed && 'Guides V2'}
                      </Tab>
                    </Tooltip>
                  ) : (
                    <Tab value="GuidesV2" icon={getTabIcon('GuidesV2')}>
                      Guides V2
                    </Tab>
                  )}
                </>
              )}
              {hasCalendarManagementPermission(permissions) &&
                (isNavCollapsed ? (
                  <Tooltip content="Calendar" relationship="label">
                    <Tab value="Calendar" icon={getTabIcon('Calendar')}>
                      {!isNavCollapsed && 'Calendar'}
                    </Tab>
                  </Tooltip>
                ) : (
                  <Tab value="Calendar" icon={getTabIcon('Calendar')}>
                    Calendar
                  </Tab>
                ))}
              {hasCalendarManagementAdminPermission(permissions) &&
                (isNavCollapsed ? (
                  <Tooltip content="Calendar Admin" relationship="label">
                    <Tab value="CalendarAdmin" icon={getTabIcon('CalendarAdmin')}>
                      {!isNavCollapsed && 'Calendar Admin'}
                    </Tab>
                  </Tooltip>
                ) : (
                  <Tab value="CalendarAdmin" icon={getTabIcon('CalendarAdmin')}>
                    Calendar Admin
                  </Tab>
                ))}
              {hasCalendarManagementAdminPermission(permissions) &&
                (isNavCollapsed ? (
                  <Tooltip content="Path & Circle Admin" relationship="label">
                    <Tab value="PathAndCircleAdmin" icon={getTabIcon('PathAndCircleAdmin')}>
                      {!isNavCollapsed && 'Path & Circle Admin'}
                    </Tab>
                  </Tooltip>
                ) : (
                  <Tab value="PathAndCircleAdmin" icon={getTabIcon('PathAndCircleAdmin')}>
                    Path & Circle Admin
                  </Tab>
                ))}
              {hasBlueprintManagementPermission(permissions) &&
                (isNavCollapsed ? (
                  <Tooltip content="Manage Blueprint" relationship="label">
                    <Tab
                      value="BlueprintFlowController"
                      icon={getTabIcon('BlueprintFlowController')}
                    >
                      {!isNavCollapsed && 'Manage Blueprint'}
                    </Tab>
                  </Tooltip>
                ) : (
                  <Tab
                    value="BlueprintFlowController"
                    icon={getTabIcon('BlueprintFlowController')}
                  >
                    Manage Blueprint
                  </Tab>
                ))}
              {hasBlueprintBuilderPermission(permissions) &&
                (isNavCollapsed ? (
                  <Tooltip content="Blueprint Builder" relationship="label">
                    <Tab value="BlueprintBuilder" icon={getTabIcon('BlueprintBuilder')}>
                      {!isNavCollapsed && 'Blueprint Builder'}
                    </Tab>
                  </Tooltip>
                ) : (
                  <Tab value="BlueprintBuilder" icon={getTabIcon('BlueprintBuilder')}>
                    Blueprint Builder
                  </Tab>
                ))}
              {hasBlueprintBuilderPermission(permissions) &&
                (isNavCollapsed ? (
                  <Tooltip content="Backfill Assessment" relationship="label">
                    <Tab value="BackfillAssessment" icon={getTabIcon('BackfillAssessment')}>
                      {!isNavCollapsed && 'Backfill Assessment'}
                    </Tab>
                  </Tooltip>
                ) : (
                  <Tab value="BackfillAssessment" icon={getTabIcon('BackfillAssessment')}>
                    Backfill Assessment
                  </Tab>
                ))}
              {hasPromptManagementPermission(permissions) &&
                (isNavCollapsed ? (
                  <Tooltip content="Prompt Management" relationship="label">
                    <Tab value="PromptManagement" icon={getTabIcon('PromptManagement')}>
                      {!isNavCollapsed && 'Prompt Management'}
                    </Tab>
                  </Tooltip>
                ) : (
                  <Tab value="PromptManagement" icon={getTabIcon('PromptManagement')}>
                    Prompt Management
                  </Tab>
                ))}
              {hasLeadManagementPermission(permissions) &&
                (isNavCollapsed ? (
                  <Tooltip content="Leads" relationship="label">
                    <Tab value="Leads" icon={getTabIcon('Leads')}>
                      {!isNavCollapsed && 'Leads'}
                    </Tab>
                  </Tooltip>
                ) : (
                  <Tab value="Leads" icon={getTabIcon('Leads')}>
                    Leads
                  </Tab>
                ))}
              {hasRoleManagemetPermission(permissions) &&
                (isNavCollapsed ? (
                  <Tooltip content="Role Manager" relationship="label">
                    <Tab value="RoleManager" icon={getTabIcon('RoleManager')}>
                      {!isNavCollapsed && 'Role Manager'}
                    </Tab>
                  </Tooltip>
                ) : (
                  <Tab value="RoleManager" icon={getTabIcon('RoleManager')}>
                    Role Manager
                  </Tab>
                ))}
              {hasDeveloperPermission(permissions) && (
                <>
                  {isNavCollapsed ? (
                    <Tooltip content="Environment Debug" relationship="label">
                      <Tab value="EnvironmentDebug" icon={getTabIcon('EnvironmentDebug')}>
                        {!isNavCollapsed && 'Environment Debug'}
                      </Tab>
                    </Tooltip>
                  ) : (
                    <Tab value="EnvironmentDebug" icon={getTabIcon('EnvironmentDebug')}>
                      Environment Debug
                    </Tab>
                  )}
                  {isNavCollapsed ? (
                    <Tooltip content="Config Management" relationship="label">
                      <Tab value="ConfigManagement" icon={getTabIcon('ConfigManagement')}>
                        {!isNavCollapsed && 'Config Management'}
                      </Tab>
                    </Tooltip>
                  ) : (
                    <Tab value="ConfigManagement" icon={getTabIcon('ConfigManagement')}>
                      Config Management
                    </Tab>
                  )}
                </>
              )}
              {hasDeveloperPermission(permissions) &&
                impersonationUnlocked &&
                (isNavCollapsed ? (
                  <Tooltip content="🎭 Impersonation" relationship="label">
                    <Tab value="Impersonation" icon={<>🎭</>}>
                      {!isNavCollapsed && '🎭 Impersonation'}
                    </Tab>
                  </Tooltip>
                ) : (
                  <Tab value="Impersonation" icon={<>🎭</>}>
                    🎭 Impersonation
                  </Tab>
                ))}
            </>
          )}
        </TabList>
      </div>

      <div className={styles.tabContent}>{renderTabContent()}</div>

      {/* Full-page modal for Path & Circle Admin Portal */}
      <Dialog
        open={isPathAndCircleModalOpen}
        onOpenChange={(_, data) => setIsPathAndCircleModalOpen(data.open)}
      >
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody className={styles.dialogBody}>
            <div className={styles.modalHeader}>
              <div className={styles.modalTitle}>Path & Circle Admin Portal</div>
              <Button
                appearance="subtle"
                icon={<Dismiss24Regular />}
                onClick={() => setIsPathAndCircleModalOpen(false)}
                aria-label="Close"
              />
            </div>
            <div className={styles.modalContent}>
              <React.Suspense fallback={<div>Loading...</div>}>
                <LazyPathAndCircleEmbed />
              </React.Suspense>
            </div>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};
